"use client";

import { useState } from "react";
import Image from "next/image";
import { canOptimizeImage } from "@/lib/image";
import { cn } from "@/lib/utils";
import { Reveal } from "../../../components/cambodia-food-expo/ui/Reveal";
import type { EventPhoto, EventProgram } from "./types";
import { PhotoLightbox } from "./event-photo-gallery";
import { useText } from "./shared";

const PHOTO_BUTTON =
  "group relative block overflow-hidden rounded-[18px] border border-[var(--event-line)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63]";

export function EventIntro({ intro }: { intro: EventProgram["intro"] }) {
  const { text } = useText();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const photos: EventPhoto[] = intro.images
    .slice(0, 3)
    .map((src) => ({ src, alt: intro.eyebrow }));
  const [main, ...rest] = photos;
  return (
    <section
      id="event-overview"
      aria-labelledby="event-overview-title"
      className="scroll-mt-36 py-16 md:py-24"
    >
      <div className="mx-auto grid w-full max-w-[1280px] items-center gap-10 px-5 sm:px-6 lg:grid-cols-[1fr_1.05fr] lg:gap-16">
        <Reveal>
          <h2
            id="event-overview-title"
            className="text-[1.75rem] font-bold leading-[1.45] text-[var(--event-ink)] md:text-[2.6rem]"
          >
            {text(intro.eyebrow)}
          </h2>
          <div className="mt-6 max-w-xl space-y-4 text-base leading-8 text-[var(--event-muted)] md:text-lg md:leading-9">
            {intro.body.map((paragraph) => (
              <p key={paragraph.en}>{text(paragraph)}</p>
            ))}
          </div>
        </Reveal>
        {main && (
          <Reveal delay={0.08}>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <button
                type="button"
                onClick={() => setOpenIndex(0)}
                aria-label={text(main.alt)}
                className={cn(PHOTO_BUTTON, "col-span-2 aspect-video")}
              >
                <Image
                  src={main.src}
                  unoptimized={!canOptimizeImage(main.src)}
                  alt={text(main.alt)}
                  fill
                  sizes="(min-width: 1024px) 600px, 100vw"
                  className="object-cover transition duration-500 ease-out motion-safe:group-hover:scale-105"
                />
              </button>
              {rest.map((photo, i) => (
                <button
                  key={photo.src}
                  type="button"
                  onClick={() => setOpenIndex(i + 1)}
                  aria-label={text(photo.alt)}
                  className={cn(PHOTO_BUTTON, "aspect-[4/3]")}
                >
                  <Image
                    src={photo.src}
                    unoptimized={!canOptimizeImage(photo.src)}
                    alt=""
                    fill
                    sizes="(min-width: 1024px) 300px, 50vw"
                    className="object-cover transition duration-500 ease-out motion-safe:group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </Reveal>
        )}
      </div>
      {openIndex !== null && (
        <PhotoLightbox
          items={photos}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </section>
  );
}
